import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TeamsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SectionOrderCard from "@/components/SectionOrderCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Plus, X } from "lucide-react";
import { SPORT_IDS, getSport } from "@/lib/sports";
import { useCategories } from "@/lib/categories";
import ImageInput from "@/components/ImageInput";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const empty = {
  name: "",
  sport: "football",
  club_name: "",
  category: "",
  season: "",
  logo_url: "",
  cover_image_url: "",
  hero_image_url: "",
  primary_color: "#0066FF",
  secondary_color: "#D4AF37",
  bio: "",
  location: "",
  founded_year: null,
  achievements: [],
  highlights: [],
  gallery: [],
  social_links: {},
  section_order: [],
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-overline !text-zinc-400">{label}</Label>
    {children}
  </div>
);

export default function AdminTeam() {
  const categories = useCategories();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== "new";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    TeamsAPI.get(id)
      .then((p) => setForm({ ...empty, ...p.team }))
      .catch(() => toast.error("Équipe introuvable"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateAch = (i, patch) =>
    setForm((f) => ({
      ...f,
      achievements: f.achievements.map((a, idx) =>
        idx === i ? { ...a, ...patch } : a,
      ),
    }));
  const addAch = () =>
    setForm((f) => ({
      ...f,
      achievements: [...f.achievements, { date: "", title: "", description: "" }],
    }));
  const removeAch = (i) =>
    setForm((f) => ({
      ...f,
      achievements: f.achievements.filter((_, idx) => idx !== i),
    }));

  const updateGallery = (i, url) =>
    setForm((f) => ({
      ...f,
      gallery: f.gallery.map((g, idx) => (idx === i ? { ...g, url } : g)),
    }));
  const addGallery = () =>
    setForm((f) => ({ ...f, gallery: [...f.gallery, { url: "" }] }));
  const removeGallery = (i) =>
    setForm((f) => ({
      ...f,
      gallery: f.gallery.filter((_, idx) => idx !== i),
    }));

  const updateHighlight = (i, patch) =>
    setForm((f) => ({
      ...f,
      highlights: f.highlights.map((h, idx) =>
        idx === i ? { ...h, ...patch } : h,
      ),
    }));
  const addHighlight = () =>
    setForm((f) => ({
      ...f,
      highlights: [
        ...f.highlights,
        { title: "", type: "youtube", url: "" },
      ],
    }));
  const removeHighlight = (i) =>
    setForm((f) => ({
      ...f,
      highlights: f.highlights.filter((_, idx) => idx !== i),
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Le nom de l'équipe est requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        founded_year: form.founded_year ? Number(form.founded_year) : null,
      };
      const result = isEdit
        ? await TeamsAPI.update(id, payload)
        : await TeamsAPI.create(payload);
      toast.success(isEdit ? "Équipe mise à jour" : "Équipe créée");
      navigate(`/team/${result.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer cette équipe ?")) return;
    try {
      await TeamsAPI.remove(id);
      toast.success("Équipe supprimée");
      navigate("/admin/teams");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 md:px-8 py-10 pb-mobile-safe">
        <Link
          to="/admin/teams"
          data-testid="admin-team-back"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour aux équipes
        </Link>

        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mb-2">
          {isEdit ? "Modifier l'équipe" : "Nouvelle équipe"}
        </h1>
        <p className="text-zinc-400 font-body text-sm mb-8">
          Crée la fiche complète d'une équipe (logo, palmarès, galerie). Lie
          les joueurs depuis leur fiche individuelle.
        </p>

        {/* Identité */}
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-2 mb-4">
          Identité
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Field label="Nom de l'équipe">
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex : Servette FC U17"
              data-testid="team-name"
              className={inputClass}
              required
            />
          </Field>
          <Field label="Club parent (multisport)">
            <Input
              value={form.club_name}
              onChange={(e) => set("club_name", e.target.value)}
              placeholder="Ex : Servette FC"
              data-testid="team-club"
              className={inputClass}
            />
          </Field>
          <Field label="Sport">
            <Select
              value={form.sport}
              onValueChange={(v) => set("sport", v)}
            >
              <SelectTrigger
                data-testid="team-sport"
                className={inputClass}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPORT_IDS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {getSport(s).label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Catégorie">
            <Input
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              placeholder="U17, Senior…"
              data-testid="team-category"
              className={inputClass}
              list="category-options"
            />
            <datalist id="category-options">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label="Saison">
            <Input
              value={form.season}
              onChange={(e) => set("season", e.target.value)}
              placeholder="2024/2025"
              data-testid="team-season"
              className={inputClass}
            />
          </Field>
          <Field label="Année de fondation">
            <Input
              type="number"
              value={form.founded_year ?? ""}
              onChange={(e) => set("founded_year", e.target.value)}
              placeholder="1908"
              className={inputClass}
            />
          </Field>
          <Field label="Localisation">
            <Input
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="Genève, Suisse"
              className={inputClass}
            />
          </Field>
          <Field label="Couleur primaire">
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => set("primary_color", e.target.value)}
                className="w-12 h-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <Input
                value={form.primary_color}
                onChange={(e) => set("primary_color", e.target.value)}
                className={inputClass}
              />
            </div>
          </Field>
        </div>

        {/* Visuels */}
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-6 mb-4">
          Visuels
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Field label="Logo">
            <ImageInput
              value={form.logo_url}
              onChange={(v) => set("logo_url", v)}
              testId="img-team-logo"
              size="sm"
              placeholder="https://…"
            />
          </Field>
          <Field label="Image de fond (cover)">
            <ImageInput
              value={form.cover_image_url}
              onChange={(v) => set("cover_image_url", v)}
              testId="img-team-cover"
              aspect="wide"
              placeholder="https://…"
            />
          </Field>
          <Field label="Image hero">
            <ImageInput
              value={form.hero_image_url}
              onChange={(v) => set("hero_image_url", v)}
              testId="img-team-hero"
              aspect="wide"
              placeholder="https://…"
            />
          </Field>
        </div>

        {/* Bio */}
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-6 mb-4">
          Présentation
        </h2>
        <div className="mb-6">
          <Textarea
            value={form.bio}
            onChange={(e) => set("bio", e.target.value)}
            placeholder="Histoire, mission, palmarès en quelques lignes…"
            rows={4}
            className={inputClass}
          />
        </div>

        {/* Palmarès */}
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">
            Palmarès
          </h2>
          <Button
            type="button"
            onClick={addAch}
            variant="outline"
            size="sm"
            className="border-white/15 text-zinc-300"
          >
            <Plus className="w-3 h-3 mr-1.5" /> Ajouter
          </Button>
        </div>
        <div className="space-y-3 mb-6">
          {form.achievements.map((a, i) => (
            <div
              key={i}
              className="metal-card rounded-xl p-3 grid grid-cols-1 md:grid-cols-[100px,1fr,2fr,auto] gap-2 items-start"
            >
              <Input
                value={a.date}
                onChange={(e) => updateAch(i, { date: e.target.value })}
                placeholder="2024"
                className={inputClass}
              />
              <Input
                value={a.title}
                onChange={(e) => updateAch(i, { title: e.target.value })}
                placeholder="Champion U17"
                className={inputClass}
              />
              <Input
                value={a.description}
                onChange={(e) =>
                  updateAch(i, { description: e.target.value })
                }
                placeholder="Description"
                className={inputClass}
              />
              <Button
                type="button"
                onClick={() => removeAch(i)}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
          {form.achievements.length === 0 && (
            <p className="text-zinc-500 text-xs font-body">Aucun titre.</p>
          )}
        </div>

        {/* Highlights */}
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">
            Highlights vidéo
          </h2>
          <Button
            type="button"
            onClick={addHighlight}
            variant="outline"
            size="sm"
            className="border-white/15 text-zinc-300"
          >
            <Plus className="w-3 h-3 mr-1.5" /> Ajouter
          </Button>
        </div>
        <div className="space-y-3 mb-6">
          {form.highlights.map((h, i) => (
            <div
              key={i}
              className="metal-card rounded-xl p-3 grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2 items-start"
            >
              <Input
                value={h.title}
                onChange={(e) =>
                  updateHighlight(i, { title: e.target.value })
                }
                placeholder="Titre"
                className={inputClass}
              />
              <Input
                value={h.url}
                onChange={(e) => updateHighlight(i, { url: e.target.value })}
                placeholder="URL YouTube/Vimeo/MP4"
                className={inputClass}
              />
              <Button
                type="button"
                onClick={() => removeHighlight(i)}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>

        {/* Galerie */}
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">
            Galerie
          </h2>
          <Button
            type="button"
            onClick={addGallery}
            variant="outline"
            size="sm"
            className="border-white/15 text-zinc-300"
          >
            <Plus className="w-3 h-3 mr-1.5" /> Ajouter
          </Button>
        </div>
        <div className="space-y-3 mb-8">
          {form.gallery.map((g, i) => (
            <div
              key={i}
              className="flex gap-3 items-start metal-card rounded-xl p-3"
            >
              <div className="flex-1 min-w-0">
                <ImageInput
                  value={g.url}
                  onChange={(v) => updateGallery(i, v)}
                  testId={`img-team-gallery-${i}`}
                  size="md"
                  placeholder="URL image"
                />
              </div>
              <Button
                type="button"
                onClick={() => removeGallery(i)}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 mt-1"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-3 mb-8">
          <h3 className="font-display text-lg font-bold uppercase tracking-wide">
            Ordre des sections de la page
          </h3>
          <SectionOrderCard
            page="team"
            value={form.section_order}
            onChange={(v) => set("section_order", v)}
          />
        </div>

        <div className="flex flex-wrap gap-3 sticky-cta metal-card rounded-xl p-3 border border-white/15">
          <Button
            type="submit"
            disabled={saving}
            data-testid="team-submit"
            className="text-black font-bold uppercase tracking-[0.18em] text-xs h-11 px-7"
            style={{
              background:
                "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
            }}
          >
            <Save className="w-3.5 h-3.5 mr-2" />
            {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer"}
          </Button>
          {isEdit && (
            <Button
              type="button"
              onClick={onDelete}
              variant="outline"
              data-testid="team-delete-btn"
              className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
